// ============================================================
// CLUBHUB – Bezeichnungen fuer alle Auswahlwerte (eine Quelle)
// ============================================================
// Jede Maske zeigt Enums mit DIESEN Worten — damit „Lastschrift“ nicht
// in einem Modul „SEPA“ und im anderen „Einzug“ heisst. `ton` ordnet
// Status-Werte den Status-Farben der Foundation zu (Badge-Varianten).
// Browsertauglich, keine Server-Importe.
// ============================================================
import type {
  BillingRunStatus,
  CommunicationChannel,
  ContactRelation,
  ContactStatus,
  DeliveryStatus,
  DonationKind,
  FeeInterval,
  FormKind,
  Gender,
  GroupKind,
  ItemType,
  LetterSalutation,
  MaritalStatus,
  SepaSequence,
  InvoiceKind,
  InvoiceStatus,
  LedgerAccountType,
  MailingChannel,
  MailingStatus,
  MandateStatus,
  MandateType,
  PaymentMethod,
  PostAudience,
  RsvpStatus,
  Salutation,
  SubmissionStatus,
  TaskStatus,
  TaxSphere,
  TemplateKind,
  Visibility,
} from "@/generated/prisma/enums";
import { RW, wert } from "@/lib/rechtswerte";

export type Ton = "neutral" | "success" | "warning" | "danger" | "review" | "accent" | "info";

export const CONTACT_STATUS: Record<ContactStatus, { label: string; ton: Ton }> = {
  ACTIVE: { label: "Aktiv", ton: "success" },
  PASSIVE: { label: "Passiv", ton: "info" },
  HONORARY: { label: "Ehrenmitglied", ton: "accent" },
  PROSPECT: { label: "Interessent", ton: "warning" },
  FORMER: { label: "Ausgetreten", ton: "neutral" },
  DECEASED: { label: "Verstorben", ton: "neutral" },
  CONTACT: { label: "Kein Mitglied", ton: "neutral" },
};

export const SALUTATION: Record<Salutation, string> = {
  HERR: "Herr",
  FRAU: "Frau",
  DIVERS: "Divers",
  KEINE: "Keine Anrede",
  FIRMA: "Firma",
  HERR_UND_FRAU: "Herr und Frau",
  FAMILIE: "Familie",
  ELTERN: "An die Eltern von",
};

export const LETTER_SALUTATION: Record<LetterSalutation, string> = {
  LIEBE: "Liebe(r) Vorname",
  LIEBE_FORMELL: "Liebe(r) Herr/Frau Nachname",
  SEHR_GEEHRTE: "Sehr geehrte(r) Herr/Frau Nachname",
  DAMEN_HERREN: "Sehr geehrte Damen und Herren",
};

export const MARITAL_STATUS: Record<MaritalStatus, string> = {
  SINGLE: "ledig",
  MARRIED: "verheiratet",
  DIVORCED: "geschieden",
  WIDOWED: "verwitwet",
};

export const SEPA_SEQUENCE: Record<SepaSequence, { label: string; code: string }> = {
  FIRST: { label: "Erst-Lastschrift", code: "FRST" },
  RECURRING: { label: "Folge-Lastschrift", code: "RCUR" },
  FINAL: { label: "Letzte Lastschrift", code: "FNAL" },
  ONE_OFF: { label: "Einmal-Lastschrift", code: "OOFF" },
};

/** Art einer Belegzeile — Werte wie im Agent. */
export const ITEM_TYPE: Record<ItemType, string> = {
  DETAIL: "Position",
  LUMP_SUM: "Pauschale",
  BUNDLE: "Sammelposition",
  SUBTOTAL: "Zwischensumme",
  HEADING: "Überschrift",
  DESCRIPTION: "Text",
  SEPARATOR: "Trennlinie",
  PAGE_BREAK: "Seitenumbruch",
  VOLUNTARY: "Freiwilliger Beitrag",
};

export const GENDER: Record<Gender, string> = {
  MALE: "männlich",
  FEMALE: "weiblich",
  DIVERSE: "divers",
  UNKNOWN: "keine Angabe",
};

export const CONTACT_RELATION: Record<ContactRelation, string> = {
  SELF: "Eigene Person",
  GUARDIAN: "Sorgeberechtigt",
  HOUSEHOLD: "Gleicher Haushalt",
};

export const COMMUNICATION: Record<CommunicationChannel, string> = {
  EMAIL: "E-Mail",
  POST: "Post",
};

export const GROUP_KIND: Record<GroupKind, string> = {
  TEAM: "Mannschaft",
  DEPARTMENT: "Abteilung",
  BOARD: "Vorstand",
  COMMITTEE: "Ausschuss",
  SPONSOR: "Sponsoren",
  FAN: "Fans",
  OTHER: "Sonstige",
};

export const FEE_INTERVAL: Record<FeeInterval, string> = {
  MONTHLY: "monatlich",
  QUARTERLY: "vierteljährlich",
  HALF_YEARLY: "halbjährlich",
  YEARLY: "jährlich",
  ONCE: "einmalig",
};

export const PAYMENT_METHOD: Record<PaymentMethod, string> = {
  TRANSFER: "Überweisung",
  SEPA: "Lastschrift",
  CASH: "Bar",
  PAYPAL: "PayPal",
  CARD: "Karte",
  OTHER: "Sonstige",
};

export const MANDATE_TYPE: Record<MandateType, string> = {
  CORE: "SEPA-Basislastschrift",
  B2B: "SEPA-Firmenlastschrift",
};

export const MANDATE_STATUS: Record<MandateStatus, { label: string; ton: Ton }> = {
  ACTIVE: { label: "Gültig", ton: "success" },
  REVOKED: { label: "Widerrufen", ton: "danger" },
  EXPIRED: { label: "Verfallen", ton: "warning" },
};

export const INVOICE_STATUS: Record<InvoiceStatus, { label: string; ton: Ton }> = {
  DRAFT: { label: "Entwurf", ton: "neutral" },
  OPEN: { label: "Offen", ton: "info" },
  PARTIALLY_PAID: { label: "Teilweise bezahlt", ton: "warning" },
  PAID: { label: "Bezahlt", ton: "success" },
  OVERDUE: { label: "Überfällig", ton: "danger" },
  CANCELLED: { label: "Storniert", ton: "neutral" },
};

export const INVOICE_KIND: Record<InvoiceKind, string> = {
  FEE: "Beitrag",
  GENERAL: "Rechnung",
  CANCELLATION: "Stornorechnung",
  CREDIT_NOTE: "Gutschrift",
};

export const BILLING_RUN_STATUS: Record<BillingRunStatus, { label: string; ton: Ton }> = {
  DRAFT: { label: "Entwurf", ton: "neutral" },
  CREATED: { label: "Rechnungen erstellt", ton: "info" },
  SENT: { label: "Versendet", ton: "info" },
  EXPORTED: { label: "SEPA exportiert", ton: "review" },
  CLOSED: { label: "Abgeschlossen", ton: "success" },
};

export const LEDGER_TYPE: Record<LedgerAccountType, string> = {
  INCOME: "Einnahmen",
  EXPENSE: "Ausgaben",
  ASSET: "Vermögen",
  LIABILITY: "Verbindlichkeiten",
  EQUITY: "Eigenkapital",
  CLOSING: "Abschlusskonten",
};

/** Die vier Sphaeren des gemeinnuetzigen Vereins. */
export const TAX_SPHERE: Record<TaxSphere, { label: string; kurz: string; hinweis: string }> = {
  IDEELL: { label: "Ideeller Bereich", kurz: "Ideell", hinweis: "Beiträge, Spenden, Zuschüsse — steuerfrei" },
  VERMOEGEN: { label: "Vermögensverwaltung", kurz: "Vermögen", hinweis: "Zinsen, Vermietung, Verpachtung" },
  ZWECKBETRIEB: { label: "Zweckbetrieb", kurz: "Zweckbetrieb", hinweis: "Sportveranstaltungen, Kurse (§ 65, § 67a AO)" },
  WIRTSCHAFTLICH: {
    label: "Wirtschaftlicher Geschäftsbetrieb",
    kurz: "Wirtschaftlich",
    // Freigrenze aus der Rechtswerte-Tabelle (seit 01.01.2026: 50.000 €, § 64 Abs. 3 AO).
    hinweis: `Verkauf, Werbung, Feste — steuerpflichtig, wenn die Einnahmen inkl. USt ${wert(RW.STEUER_FREIGRENZE_WGB_EUR).toLocaleString("de-DE")} € im Jahr übersteigen`,
  },
};

export const RSVP_STATUS: Record<RsvpStatus, { label: string; ton: Ton }> = {
  PENDING: { label: "Offen", ton: "neutral" },
  YES: { label: "Zusage", ton: "success" },
  NO: { label: "Absage", ton: "danger" },
  MAYBE: { label: "Vielleicht", ton: "warning" },
};

export const VISIBILITY: Record<Visibility, { label: string; hinweis: string }> = {
  PUBLIC: { label: "Öffentlich", hinweis: "Jeder, auch auf der Website" },
  MEMBERS: { label: "Mitglieder", hinweis: "Alle angemeldeten Mitglieder" },
  GROUPS: { label: "Gruppen", hinweis: "Nur die ausgewählten Gruppen" },
  BOARD: { label: "Vorstand", hinweis: "Nur Vorstand und Verwaltung" },
  PRIVATE: { label: "Privat", hinweis: "Nur du" },
};

export const MAILING_CHANNEL: Record<MailingChannel, string> = {
  EMAIL: "E-Mail",
  SMS: "SMS",
  LETTER: "Brief",
};

export const MAILING_STATUS: Record<MailingStatus, { label: string; ton: Ton }> = {
  DRAFT: { label: "Entwurf", ton: "neutral" },
  SCHEDULED: { label: "Geplant", ton: "info" },
  SENDING: { label: "Wird versendet", ton: "warning" },
  SENT: { label: "Versendet", ton: "success" },
  FAILED: { label: "Fehlgeschlagen", ton: "danger" },
};

export const DELIVERY_STATUS: Record<DeliveryStatus, { label: string; ton: Ton }> = {
  PENDING: { label: "Ausstehend", ton: "neutral" },
  SENT: { label: "Gesendet", ton: "info" },
  DELIVERED: { label: "Zugestellt", ton: "success" },
  OPENED: { label: "Geöffnet", ton: "success" },
  BOUNCED: { label: "Unzustellbar", ton: "danger" },
  FAILED: { label: "Fehler", ton: "danger" },
};

export const TEMPLATE_KIND: Record<TemplateKind, string> = {
  EMAIL: "E-Mail",
  LETTER: "Brief",
  SMS: "SMS",
  INVOICE_TEXT: "Rechnungstext",
  DUNNING: "Mahnung",
  DONATION_RECEIPT: "Zuwendungsbestätigung",
  WELCOME: "Begrüßung",
};

export const DONATION_KIND: Record<DonationKind, string> = {
  MONEY: "Geldspende",
  IN_KIND: "Sachspende",
  WAIVER: "Aufwandsverzicht",
};

export const POST_AUDIENCE: Record<PostAudience, string> = {
  ALL: "Alle Mitglieder",
  BOARD: "Vorstand",
  GROUP: "Eine Gruppe",
};

export const TASK_STATUS: Record<TaskStatus, { label: string; ton: Ton }> = {
  OPEN: { label: "Offen", ton: "neutral" },
  IN_PROGRESS: { label: "In Arbeit", ton: "info" },
  DONE: { label: "Erledigt", ton: "success" },
};

export const FORM_KIND: Record<FormKind, string> = {
  MEMBERSHIP_APPLICATION: "Aufnahmeantrag",
  EVENT_REGISTRATION: "Anmeldung zu einem Termin",
  CONTACT: "Kontaktformular",
  CUSTOM: "Eigenes Formular",
};

export const SUBMISSION_STATUS: Record<SubmissionStatus, { label: string; ton: Ton }> = {
  NEW: { label: "Neu", ton: "warning" },
  ACCEPTED: { label: "Angenommen", ton: "success" },
  REJECTED: { label: "Abgelehnt", ton: "neutral" },
};

/** Badge-Klassen je Ton (Foundation-Token, kein Hex). */
export const TON_KLASSEN: Record<Ton, string> = {
  neutral: "bg-bg-tertiary text-text-secondary",
  success: "bg-success-subtle text-success",
  warning: "bg-warning-subtle text-warning",
  danger: "bg-danger-subtle text-danger",
  review: "bg-review-subtle text-review",
  accent: "bg-accent-subtle text-accent",
  info: "bg-accent-2-subtle text-accent-2",
};
